// The shared client layer for 2-player board games (chess, checkers): each
// game supplies a BoardGameUI adapter; the overlay, the 3D board and the VR
// panel in this folder do the rest. Selection (picked piece / partial
// multi-jump / pending promotion) is shared per table, so a tap on the 3D
// board and the 2D board agree.

import type { ComponentType, ReactNode } from 'react';
import { create } from 'zustand';
import { tableAction, type TableState } from '../gameStore';

/** A piece on a square. `side` = the seat that owns it. */
export interface PieceSpec { sq: number; kind: string; side: 0 | 1 }

/** What the picker needs for the current position (null = you can't move now). */
export interface PickRules {
  /** Changes whenever the position changes (resets a stale selection). */
  key: string;
  /** Squares holding a piece that can move. */
  starts: Set<number>;
  /** Next squares after this partial path (path[0] = the picked piece). */
  next(path: number[]): number[];
  /** A finished path → the move to send, 'promo' (ask which piece), or null (keep picking). */
  finish(path: number[]): object | 'promo' | null;
  /** The move for a promotion choice. */
  promote?(path: number[], piece: string): object;
}

export interface LobbyOption { key: string; label: string; values: { v: string; label: string }[] }

export interface BoardGameUI<G = any> {
  /** Lobby options (level, clock) and their defaults. */
  options: LobbyOption[];
  defaults: Record<string, string>;
  pieces(g: G): PieceSpec[];
  /** Squares of the last move (from … to). */
  last(g: G): number[];
  /** The checked king's square, if any. */
  check(g: G): number | null;
  /** Whose turn (seat) or null when over. */
  turn(g: G): number | null;
  /** Legal-move picker for `seat` (null if it isn't their move). */
  rules(g: G, seat: number): PickRules | null;
  moves(g: G): string[];
  /** Plies per numbered move line ("1. e4 e5"). */
  result(g: G): { winner: number | null; text: string } | null;
  drawOffer(g: G): number | null;
  /** Clock ms left per seat at `now`, or null when untimed. */
  clocks?(g: G, now: number): [number, number] | null;
  promoChoices?: { v: string; label: string }[];
  /** 2D glyph for a piece (overlay). */
  glyph(p: PieceSpec): ReactNode;
  /** 3D model of a piece (on its square, origin at the board surface). */
  Piece: ComponentType<{ kind: string; side: 0 | 1 }>;
}

interface Sel { key: string; path: number[]; promo: number[] | null }
interface SelStore { sel: Record<string, Sel | undefined>; set(pid: string, s: Sel | undefined): void }
export const useBoardSel = create<SelStore>((set) => ({
  sel: {},
  set: (pid, s) => set((st) => ({ sel: { ...st.sel, [pid]: s } })),
}));

/** Selection + tap handling for one table, shared by every view of it. */
export function useBoardPicker(pid: string, rules: PickRules | null) {
  const raw = useBoardSel((s) => s.sel[pid]);
  const cur = rules && raw && raw.key === rules.key ? raw : null;
  const path = cur?.path ?? [];
  const promo = cur?.promo ?? null;
  const targets = rules && path.length && !promo ? rules.next(path) : [];
  const setSel = (s: Sel | undefined) => useBoardSel.getState().set(pid, s);
  const send = (move: object) => { setSel(undefined); void tableAction(pid, 'move', { move }); };

  const tap = (sq: number) => {
    if (!rules || promo) return;
    if (path.length && rules.next(path).includes(sq)) {
      const np = [...path, sq];
      const f = rules.finish(np);
      if (f === 'promo') setSel({ key: rules.key, path: np, promo: np });
      else if (f) send(f);
      else setSel({ key: rules.key, path: np, promo: null });
      return;
    }
    if (path.length === 1 && path[0] === sq) { setSel(undefined); return; }
    if (rules.starts.has(sq)) setSel({ key: rules.key, path: [sq], promo: null });
    else setSel(undefined);
  };
  const choosePromo = (piece: string) => {
    if (rules?.promote && promo) send(rules.promote(promo, piece));
  };
  const clear = () => setSel(undefined);
  return { path, targets, promo, tap, choosePromo, clear, canMove: !!rules };
}

/** Current lobby option values. */
export function optionValues(ui: BoardGameUI, st: TableState): Record<string, string> {
  return { ...ui.defaults, ...(st.options ?? {}) };
}

export const fmtClock = (ms: number) => {
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** "1. e4 e5  2. Nf3 …" */
export function moveLines(moves: string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < moves.length; i += 2) out.push(`${i / 2 + 1}. ${moves[i]}${moves[i + 1] ? ` ${moves[i + 1]}` : ''}`);
  return out;
}
